<template>
  <v-container mt-7>
    <v-form ref="form" lazy-validation>
      <v-row>
        <v-col class="selectorColumn">
          <v-select
            v-model="orgChoice"
            :items="organizations"
            label="Organizations"
            :disabled="orgDisabled"
            outlined
            dense
            clearable
          >
            <template v-slot:selection="{ item, index }">
              <span>{{ item }}</span>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ orgChoice.length - 1 }}</span
              >
            </template>
          </v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-select
            v-model="insChoice"
            :items="insurances"
            required
            :rules="[this.insChoice.length !== 0 || 'Insurance is required']"
            item-text="Insurance"
            return-object
            label="Select Insurance"
            outlined
            dense
            clearable
            @click:clear="$nextTick(() => (insChoice = []))"
          ></v-select>
        </v-col>
        <v-col class="selectorColumn" v-if="!groupDisabled">
          <v-autocomplete
            v-model="groupChoice"
            :items="groups"
            label="Groups"
            item-text="GROUP_NAME"
            item-value="BILLING_PROVIDER_NPI"
            :disabled="groupDisabled"
            multiple
            outlined
            dense
            clearable
            :rules="[
              (v) => {
                if (this.user.role === 'Provider' || this.user.role === 'Group') {
                  return groupChoice.length > 0 || 'Please select your group';
                } else {
                  return true;
                }
              },
            ]"
            class="newSelect"
          >
            <template v-slot:selection="{ item, index }">
              <div class="selection-text" v-if="index === 0">{{ item.GROUP_NAME }}</div>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ groupChoice.length - 1 }}</span
              >
            </template>
          </v-autocomplete>
        </v-col>
        <v-col class="selectorColumn">
          <v-autocomplete
            v-model="provChoice"
            :items="providers"
            item-value="PROVIDER_ID"
            item-text="PROVIDER_NAME"
            :menu-props="{ maxHeight: '400' }"
            label="Providers"
            multiple
            clearable
            outlined
            dense
            :rules="[
              (v) =>
                (this.user.role === 'Provider' && provChoice.length > 0) ||
                this.user.role !== 'Provider' ||
                'Please select your provider',
            ]"
            class="newSelect"
          >
            <template v-slot:selection="{ item, index }">
              <div class="selection-text" v-if="index === 0">
                {{ item.PROVIDER_NAME }}
              </div>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ provChoice.length - 1 }}</span
              >
            </template>
          </v-autocomplete>
        </v-col>
        <v-col class="selectorColumn">
          <v-select
            v-model="locChoice"
            :items="locations"
            item-text="PROVIDER_LOCATION"
            item-value="PRACTICE_ID"
            return-object
            label="Locations"
            multiple
            outlined
            dense
            clearable
            class="newSelect"
          >
            <template v-slot:selection="{ item, index }">
              <div class="selection-text" v-if="index === 0">
                {{ item.PROVIDER_LOCATION || item.SERVICE_LOCATION_NAME || 'No Location Provided' }}
              </div>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ locChoice.length - 1 }}</span
              >
            </template>
          </v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-select
            v-model="reportChoice"
            :items="reports"
            :rules="[this.reportChoice.length !== 0 || 'Report is required']"
            label="Select Report"
            required
            item-text="Report Type"
            return-object
            outlined
            dense
            clearable
            @click:clear="$nextTick(() => (reportChoice = []))"
          ></v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="startDate"
                label="Year"
                append-icon="mdi-calendar"
                outlined
                dense
                readonly
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="startDate"
              show-current="false"
              type="year"
              :max="new Date().toISOString().substr(0, 4)"
              @input="menu = false"
              no-title
              scrollable
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col class="selectorColumn">
          <v-btn @click="validate" color="secondary" depressed block class="py-5">
            Run Report
            <v-icon>mdi-play</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-row>
      <v-col v-if="load" cols="12" class="text-center">
        <v-progress-circular
          color="secondary"
          indeterminate
          :size="50"
          :width="3"
        ></v-progress-circular>
      </v-col>

      <v-col
        v-else-if="(insChoice.length === 0 || reportChoice.length === 0) && !showReport"
        cols="12"
      >
        <v-alert :value="true" type="info"> Please select all required fields </v-alert>
      </v-col>

      <v-col
        v-else-if="(insChoice.length > 0 || reportChoice.length > 0) && !showReport"
        cols="12"
      >
        <v-alert :value="true" type="info"> Press run report to continue </v-alert>
      </v-col>

      <v-col
        v-else-if="
          reportChoice === 'HCC Scorecard' &&
          !scorecardData.breakdown &&
          showReport &&
          !load &&
          !changeDetected
        "
        cols="12"
      >
        <v-alert :value="true" type="info">
          There is no data available for this selection. Please change your filter
          selections and run report again
        </v-alert>
      </v-col>

      <v-col
        v-else-if="
          (reportChoice === 'Open Conditions' || reportChoice === 'New Conditions') &&
          conditionData.length === 0 &&
          showReport &&
          !load &&
          !changeDetected
        "
        cols="12"
      >
        <v-alert :value="true" type="info">
          There is no data available for this selection. Please change your filter
          selections and run report again
        </v-alert>
      </v-col>

      <v-col v-else-if="showReport && changeDetected" cols="12">
        <v-alert :value="true" type="warning" dense>
          Change detected: Run the report to see updated data
        </v-alert>
      </v-col>

      <Scorecard
        v-show="showReport === 'HCC Scorecard' && scorecardData.breakdown && !load"
        :reportData="scorecardData"
        :organization="orgChoice"
        :insurance="selectedInsurance"
        :groups="groupNum"
        :providers="provChoice"
        :locations="locChoice"
        :startDate="startDate"
      />
      <OpenConditions
        v-show="showReport === 'Open Conditions' && conditionData.length > 0 && !load"
        :reportData="conditionData"
        :startDate="startDate"
        :insurance="selectedInsurance"
      />
      <NewConditions
        v-show="showReport === 'New Conditions' && conditionData.length > 0 && !load"
        :reportData="conditionData"
        :startDate="startDate"
        :insurance="selectedInsurance"
      />
    </v-row>
  </v-container>
</template>
<script>
import axios from 'axios';
import OpenConditions from '../components/codingComponents/openConditions';
import NewConditions from '../components/codingComponents/newConditions';
import Scorecard from '../components/codingComponents/Scorecard';

export default {
  components: {
    OpenConditions,
    NewConditions,
    Scorecard,
  },
  data() {
    return {
      startDate: new Date(new Date().getFullYear(), 0).toISOString().substr(0, 4),
      menu: false,
      user: '',
      orgChoice: [],
      groupChoice: [],
      provChoice: [],
      locChoice: [],
      insChoice: [],
      reportChoice: [],
      reports: ['Open Conditions', 'New Conditions'],
      organizations: [],
      insurances: [],
      groups: [],
      providers: [],
      locations: [],
      groupObject: [],
      groupNum: [],
      load: false,
      scorecardData: [],
      conditionData: [],
      showReport: false,
      changeDetected: false,
      valid: true,
      selectedInsurance: [],
    };
  },
  mounted() {
    this.getUser();
  },
  methods: {
    validate() {
      if (this.$refs.form.validate()) {
        this.getReport();
      }
    },
    async getUser() {
      await axios
        .get('/api/user')
        .then((response) => {
          this.user = response.data[0];
        })
        .catch(err => this.$router.push('/login'));
      this.checkPresets();
    },
    checkPresets() {
      if (
        this.user.role === 'Clinical'
        || this.user.role === 'Provider'
        || this.user.role === 'Group'
        || this.user.role === 'Affiliation'
      ) {
        this.setPresets();
      } else {
        this.getOrgs();
        this.getInsurances();
      }
    },
    setPresets() {
      this.organizations = this.user.organization;
      this.groups = this.user.group;
      this.insurances = this.user.insurance;

      if (this.user.role === 'Provider' || this.user.role === 'Specialist') {
        this.insChoice = this.user.insurance[0];
        this.orgChoice = this.user.organization;
        this.groupChoice = this.user.group;
        this.getProvider(this.user.npi);
        this.location = this.user.location;
      } else if (this.user.role === 'Group') {
        this.orgChoice = this.user.organization;
        this.insChoice = this.user.insurance[0];
      } else if (this.user.role === 'Group' || this.user.role === 'Affiliation') {
        this.orgChoice = this.user.organization;
        this.insChoice = this.user.insurance[0];
      }
    },
    async getReport() {
      this.load = true;
      this.selectedInsurance = this.insChoice;
      const { orgChoice } = this;
      const { insChoice } = this;
      const providers = this.provChoice;
      const { locChoice } = this;
      const { startDate } = this;
      const { reportChoice } = this;

      const insForUrl =
        typeof insChoice === 'string'
          ? insChoice
          : insChoice && insChoice.Insurance
            ? insChoice.Insurance
            : Array.isArray(insChoice) && insChoice[0]
              ? typeof insChoice[0] === 'string'
                ? insChoice[0]
                : insChoice[0].Insurance
              : insChoice;

      this.showReport = reportChoice;

      // let providers = [];
      // if (provChoice.length > 0) {
      //   provChoice.forEach((prov) => {
      //     if (providers.indexOf(prov.Center_PCP_Number) === -1) {
      //       providers.push(prov.Center_PCP_Number);
      //     }
      //   });
      // } else {
      //   providers = [];
      // }

      let locations = [];
      if (locChoice.length > 0) {
        locChoice.forEach((loca) => {
          if (locations.indexOf(loca.SERVICE_LOCATION_NAME) === -1) {
            locations.push(loca.SERVICE_LOCATION_NAME);
          }
        });
      } else {
        locations = [];
      }

      this.changeDetected = false;

      // This will grab the correct groupNums if no groups are selected. This is for a group level login when only select groups available to the user
      if (this.user.role === 'Group' && this.groupNum.length === 0) {
        this.cancelSource = axios.CancelToken.source();
        await axios
          .get(`api/selection/${insForUrl}/group/${this.groups}`, {
            cancelToken: this.cancelSource.token,
          })
          .then((response) => {
            this.cancelSource = null;
            const { data } = response;
            data.forEach((element) => {
              if (this.insChoice) {
                if (this.groupNum.indexOf(element.Center_PCP_Number) === -1) {
                  this.groupNum.push(element.Center_PCP_Number);
                }
              }
            });
          });
      }

      const groupNumsForUrl =
        this.groupNum.length > 0 ? this.groupNum : this.groupChoice;

      let url;
      if (reportChoice === 'HCC Scorecard') {
        url = `/api/coding/scorecard/org=${orgChoice}/group=${groupNumsForUrl}/provider=${providers}/location=${locations}/${insForUrl}/${startDate}`;
      } else if (reportChoice === 'Open Conditions') {
        url = `/api/coding/openconditions/orgs=${orgChoice}/groups=${groupNumsForUrl}/providers=${providers}/locs=${locations}/${insForUrl}/${startDate}`;
      } else if (reportChoice === 'New Conditions') {
        url = `/api/coding/newconditions/orgs=${orgChoice}/groups=${groupNumsForUrl}/providers=${providers}/locs=${locations}/${insForUrl}/${startDate}`;
      }

      // Check for pending request and if so cancel old one for new incoming
      this.cancelSearch();
      // Set the CancelToken
      this.cancelSource = axios.CancelToken.source();

      if (!url) {
        this.load = false;
        return;
      }

      axios
        .get(url, { cancelToken: this.cancelSource.token })
        .then((response) => {
          if (reportChoice === 'HCC Scorecard') {
            this.scorecardData = response.data;
          } else if (
            reportChoice === 'Open Conditions'
            || reportChoice === 'New Conditions'
          ) {
            this.conditionData = response.data;
          }

          this.load = false;
          this.cancelSource = null;
        })
        .catch((err) => {
          if (!axios.isCancel(err)) {
            console.error('Coding getReport error:', err.response || err.message);
          }
          this.load = false;
          this.cancelSource = null;
        });
    },
    getOrgs() {
      axios.get('api/list/organization').then((response) => {
        this.organizations = response.data;

        this.orgChoice = this.organizations[0];
      });
    },
    async getLocations() {
      await axios.get(`api/selection/${this.provChoice}/locations`).then((response) => {
        this.locations = (response.data || []).map((loc) => ({
          ...loc,
          PROVIDER_LOCATION: loc.PROVIDER_LOCATION != null ? loc.PROVIDER_LOCATION : 'No Location Provided'
        }));
      });
    },
    async getProviders() {
      await axios.get(`api/selection/${this.groupChoice}/providers`).then((response) => {
        this.providers = response.data;
      });
    },
    async getProvider(npi) {
      await axios.get(`api/selection/single/${npi}/provider`).then((response) => {
        this.providers = response.data;
        console.log(this.providers);
      });
    },
    getInsurances() {
      axios.get('api/list/insurances').then((response) => {
        this.insurances = response.data;
      });
    },
    async getGroups() {
      // Assigns entire user.group array if only one insurance, the else if is there in case there is more than one insurance and can only access certain groups within each insurance
      if (this.user.role === 'Group' && this.insurances.length === 1) {
        await axios.get(`api/list/npi/${this.user.npi}/groups`).then((response) => {
          this.groups = response.data;
        });
      } else if (this.user.role === 'Affiliation') {
        await axios.get(`api/list/ipaId/${this.user.npi}/groups`).then((response) => {
          this.groups = response.data;
        });
      } else if (this.user.role === 'Group' && this.insurances.length > 1) {
        axios.get(`api/list/${this.insChoice}/groups`).then((response) => {
          const userObjGroups = this.user.group;
          const allPossibleGroups = response.data;
          this.groups = [];
          allPossibleGroups.forEach((grp) => {
            if (userObjGroups.indexOf(grp) !== -1) {
              this.groups.push(grp);
            }
          });
        });
      } else if (this.insChoice.length > 1) {
        await axios.get(`api/list/${this.insChoice}/groups`).then((response) => {
          this.groups = response.data;
        });
      }
    },
    cancelSearch() {
      if (this.cancelSource) {
        this.cancelSource.cancel('Start new search, stop active search');
      }
    },
  },
  watch: {
    insChoice() {
      this.groupChoice = [];
      this.provChoice = [];
      this.locChoice = [];

      if (
        this.user.role === 'Admin'
        || this.user.role === 'Superuser'
        || this.user.role === 'Group'
        || this.user.role === 'Affiliation'
      ) {
        if (this.insChoice !== []) {
          this.getGroups();
        }
      } else {
        this.insurances = this.user.insurance;

        if (this.user.role === 'Provider' || this.user.role === 'Clinical') {
          this.groupChoice = this.user.group;
        }
      }

      if (this.showReport) {
        this.changeDetected = true;
      }
    },
    async groupChoice() {
      // Populates the group selector for group level logins with one insurance and one group available
      // if (this.user.role === 'Group' && this.insurances.length === 1 && this.groups.length === 1) {
      //   this.groupChoice = this.user.group;
      // }

      // if (this.groupChoice.length > 0) {
      //   this.providers = [];
      //   this.provChoice = [];
      //   this.locations = [];
      //   this.locChoice = [];
      //   this.groupNum = [];
      //   // Check for pending request and if so cancel old one for new incoming
      //   this.cancelSearch();
      //   // Set the CancelToken
      //   this.cancelSource = axios.CancelToken.source();

      //   await axios
      //     .get(`api/selection/${this.insChoice}/group/${this.groupChoice}`, {
      //       cancelToken: this.cancelSource.token,
      //     })
      //     .then((response) => {
      //       // Set cancelsource back to null
      //       this.cancelSource = null;
      //       const { data } = response;
      //       this.groupObject = data;
      //       data.forEach((element) => {
      //         if (this.user.role === 'Provider' || this.user.role === 'Clinical') {
      //           if (this.user.provider.includes(element.Provide_Name)) {
      //             this.providers.push(element);
      //             this.provChoice.push(element);
      //           }
      //         } else {
      //           this.providers.push(element);
      //         }
      //         if (this.insChoice) {
      //           if (this.groupNum.indexOf(element.Center_PCP_Number) === -1) {
      //             this.groupNum.push(element.Center_PCP_Number);
      //           }
      //         }
      //       });
      //     });
      // } else {
      //   this.providers = [];
      //   this.provChoice = [];
      //   this.locChoice = [];
      //   this.locations = [];
      //   this.groupNum = [];
      // }

      if (this.showReport) {
        this.changeDetected = true;
      }
      if (this.groupChoice) {
        this.getProviders();
      }
    },
    provChoice() {
      if (this.showReport) {
        this.changeDetected = true;
      }

      if (this.provChoice) {
        this.getLocations();
      }

      // if (this.provChoice !== []) {
      //   const prov = this.provChoice;
      //   let allProviders = this.providers;
      //   let providerNames = [];
      //   this.locations = [];
      //   this.locChoice = [];

      //   // Create an Array of selected provider names
      //   prov.forEach((provs) => {
      //     providerNames.push(provs.Provide_Name);
      //   });
      //   // Loop through all the providers objects and see if there is a match with any names in providerNames array
      //   allProviders.forEach((p) => {
      //     if (providerNames.indexOf(p.Provide_Name) !== -1) {
      //       // Add the location
      //       if (p.Center_Location === null) {
      //         this.locations.push(`${p.Center_PCP_Number} - No Location Provided`);
      //       } else {
      //         // Add the location and add PCP_IDs, there can be multiple for a single provider
      //         this.locations.push(p.Center_Location);
      //       }
      //     }
      //   });
      // } else {
      //   this.locChoice = [];
      //   this.locations = [];
      // }
    },
    locChoice() {
      if (this.showReport) {
        this.changeDetected = true;
      }
    },
    startDate() {
      this.startDate = this.startDate.substr(0, 4);

      if (this.showReport) {
        this.changeDetected = true;
      }
    },
    reportChoice() {
      if (this.showReport) {
        this.changeDetected = true;
      }
    },
  },
  computed: {
    orgDisabled() {
      // if (
      //   this.user.role === 'Provider' ||
      //   this.user.role === 'Group' ||
      //   this.user.role === 'Clinical'
      // ) {
      //   return true;
      // }
      return false;
    },
    insDisabled() {
      // if (
      //   this.user.role === 'Provider' ||
      //   this.user.role === 'Clinical' ||
      //   this.insurances.length == 1
      // ) {
      //   return true;
      // }
      return false;
    },
    provDisabled() {
      // if (this.groupChoice.length === 0) {
      //   this.provChoice = [];
      //   return true;
      // }
      // if (this.user.role === 'Provider' || this.user.role === 'Clinical') {
      //   return true;
      // }
      return false;
    },
    locDisabled() {
      if (this.provChoice.length === 0 || this.groupChoice.length === 0) {
        this.locChoice = [];
        return true;
      }
      return false;
    },
    groupDisabled() {
      if (this.orgChoice.length === 0 || this.insChoice.length === 0) {
        this.groupChoice = [];
        return true;
      }
      if (this.user.role === 'Clinical' || this.user.role === 'Provider') {
        return true;
      }
      // if (this.user.role === 'Group' && this.groups.length === 1) {
      //   this.groupChoice = this.user.group;
      //   return true;
      // }
      return false;
    },
  },
};
</script>
